// APIs to share the calendars of advocates and company pages, and to see the calendars shared with an advocate

const DEFAULT_ERROR = "Something went wrong. Please try again in sometime."

function withQuery(path, params) {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params || {})) {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value)
    }
  }
  const search = query.toString()
  return search ? `${path}?${search}` : path
}

// request returns the data of the response, or the error and the status code
async function request(method, path, { csrfToken, query, body } = {}) {
  try {
    const options = { method }
    if (method !== "GET") {
      options.headers = { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }
    }
    if (body) {
      options.body = JSON.stringify(body)
    }
    const res = await fetch(withQuery(path, query), options)
    const data = await res.json()
    if (res.status >= 400) {
      return { error: data.Error || DEFAULT_ERROR, status: res.status }
    }
    return { data: data.Data }
  } catch (e) {
    console.error("error while calling", path, e)
    return { error: DEFAULT_ERROR }
  }
}

function calendarQuery(calendar, orgID) {
  return { calendarType: calendar.Type, calendarID: calendar.ID, orgID }
}

// fetchCalendarSharing returns who can see the calendar and the requests to see it
export async function fetchCalendarSharing({ orgID, calendar }) {
  const { data, error } = await request("GET", "/api/admin/calendar-accesses", { query: calendarQuery(calendar, orgID) })
  return error ? { error } : { sharing: { Accesses: data?.Accesses || [] } }
}

// fetchViewerCalendarAccesses returns the calendars the advocate can see or asked to see
export async function fetchViewerCalendarAccesses({ orgID, viewerID }) {
  const { data, error } = await request("GET", "/api/admin/calendar-accesses", { query: { viewerID, orgID } })
  return error ? { error } : { accesses: data?.Accesses || [] }
}

// grantCalendarAccess shares every calendar with every advocate
export async function grantCalendarAccess(csrfToken, { orgID, calendars, viewerIDs }) {
  const { data, error } = await request("POST", "/api/admin/calendar-accesses", {
    csrfToken,
    query: { orgID },
    body: { Calendars: calendars.map(c => ({ Type: c.Type, ID: c.ID })), ViewerIDs: viewerIDs },
  })
  return error ? { error } : { accesses: data?.Accesses || [] }
}

export async function approveCalendarAccess(csrfToken, { orgID, accessID }) {
  const { data, error } = await request("POST", `/api/admin/calendar-accesses/${accessID}/approve`, { csrfToken, query: { orgID } })
  return error ? { error } : { access: data }
}

export async function declineCalendarAccess(csrfToken, { orgID, accessID }) {
  const { data, error } = await request("POST", `/api/admin/calendar-accesses/${accessID}/decline`, { csrfToken, query: { orgID } })
  return error ? { error } : { access: data }
}

export async function revokeCalendarAccess(csrfToken, { orgID, accessID }) {
  const { error } = await request("DELETE", `/api/admin/calendar-accesses/${accessID}`, { csrfToken, query: { orgID } })
  return error ? { error } : {}
}

// fetchCalendarAccessRequests returns the pending requests of the organization
export async function fetchCalendarAccessRequests({ orgID }) {
  const { data, error } = await request("GET", "/api/admin/calendar-access-requests", { query: { orgID } })
  return error ? { error } : { requests: data?.Accesses || [] }
}

// fetchCalendars returns the company pages and the other advocates, with the access of the user to each
export async function fetchCalendars({ orgID }) {
  const { data, error } = await request("GET", "/api/calendars", { query: { orgID } })
  return error ? { error } : { calendars: data?.Calendars || [] }
}

// fetchMyCalendarAccesses returns the calendars the user can see or asked to see
export async function fetchMyCalendarAccesses({ orgID }) {
  const { data, error } = await request("GET", "/api/me/calendar-accesses", { query: { orgID } })
  return error ? { error } : { accesses: data?.Accesses || [] }
}

export async function requestCalendarAccess(csrfToken, { orgID, calendar }) {
  const { data, error } = await request("POST", "/api/calendar-access-requests", {
    csrfToken,
    query: { orgID },
    body: { Type: calendar.Type, ID: calendar.ID },
  })
  return error ? { error } : { access: data }
}

// fetchSharedCalendar returns the shared and scheduled shares of the calendar, with status 401 when the user can't see it
export async function fetchSharedCalendar({ orgID, calendar, from, to }) {
  const { data, error, status } = await request("GET", `/api/calendars/${calendar.Type}/${calendar.ID}/items`, {
    query: { from: from.toISOString(), to: to.toISOString(), orgID },
  })
  return error ? { error, status } : { calendar: data.Calendar, items: data.Items || [] }
}

